import { LitElement, html, css } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { Ref, createRef, ref } from 'lit/directives/ref.js';

@customElement('as-dialog')
export class Dialog extends LitElement {
  public static styles = css`
    .dialog-mask {
      background-color: rgba(0, 0, 0, 0.2);
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      z-index: 100;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
    }

    .dialog {
      width: calc(100% - 32px);
      background-color: #f2f2f2;
      box-shadow: 0 4px 5px 0 rgba(0, 0, 0, 0.14), 0 1px 10px 0 rgba(0, 0, 0, 0.12), 0 2px 4px -1px rgba(0, 0, 0, 0.4);
      padding: 16px;
      padding-top: 0;
      box-sizing: border-box;
    }

    .dialog-header {
      border-bottom: 1px solid #d9d9d9;
      padding: 8px 0;
      margin-bottom: 16px;
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: center;
    }

    .dialog-header-items {
      flex: 1 1;
      display: flex;
      flex-direction: row;
      align-items: center;
    }

    .dialog-header-items-left {
      justify-content: flex-start;
    }

    .dialog-header-items-right {
      justify-content: flex-end;
    }

    .dialog-header-caption {
      flex: 0 0 auto;
      text-align: center;
      font-family: 'Segoe UI Light', 'Segoe UI', 'Lucida Sans', 'Arial', sans-serif;
      color: #555555;
      font-size: 24px;
      font-weight: lighter;
    }

    .dialog-content {
      overflow-y: auto;
      max-height: 85vh;
    }
  `;

  @property()
  public caption!: string;

  @property()
  public visible = false;

  @property()
  public cancelCaption?: string;

  @property()
  public submitCaption?: string;

  @property()
  public maxWidth?: string = '800px';

  private cancelRef: Ref<HTMLElement> = createRef();
  private submitRef: Ref<HTMLElement> = createRef();

  public render() {
    if (!this.visible) {
      return null;
    } else {
      return html`
        <div class="dialog-mask">
          <div class="dialog" @keydown="${this.handleFocus}" style="max-width:${this.maxWidth};">
            <div class="dialog-header">
              <div class="dialog-header-items dialog-header-items-left">${this.getLeftHeaderItems()}</div>
              <div class="dialog-header-caption">${this.caption}</div>
              <div class="dialog-header-items dialog-header-items-right">${this.getRightHeaderItems()}</div>
            </div>
            <div class="dialog-content">
              <slot></slot>
            </div>
          </div>
        </div>
      `;
    }
  }

  private getLeftHeaderItems() {
    if (!this.cancelCaption) {
      return html`<slot name="left-items"></slot>`;
    } else {
      return html`
        <sd-button ${ref(this.cancelRef)} @click="${this.handleCancel}" id="as-dialog-cancel">
          ${this.cancelCaption}
        </sd-button>
        <slot name="left-items"></slot>
      `;
    }
  }

  private getRightHeaderItems() {
    if (!this.submitCaption) {
      return html`<slot name="right-items"></slot>`;
    } else {
      return html`
        <slot name="right-items"></slot>
        <sd-button ${ref(this.submitRef)} @click="${this.handleSubmit}" primary="true" id="as-dialog-submit">
          ${this.submitCaption}
        </sd-button>
      `;
    }
  }

  private handleFocus(e: KeyboardEvent) {
    const focusableElements = Array.from(
      this.querySelectorAll<HTMLElement>(
        'a[href], button, sd-button, textarea, input[type="text"], input[type="radio"], input[type="checkbox"], select'
      )
    );

    if (this.submitRef.value) {
      focusableElements.unshift(this.submitRef.value);
    }

    if (this.cancelRef.value) {
      focusableElements.unshift(this.cancelRef.value);
    }

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (e.key === 'Tab') {
      if (e.shiftKey) {
        this.handleTab(e, firstElement, lastElement);
      } else {
        this.handleTab(e, lastElement, firstElement);
      }
    }
  }

  private handleTab(e: KeyboardEvent, elementToCheck: HTMLElement, elementToFocus: HTMLElement) {
    if (document.activeElement === elementToCheck || this.shadowRoot?.activeElement === elementToCheck) {
      elementToFocus.focus();
      e.preventDefault();
    }
  }

  public showDialog() {
    this.visible = true;
    this.dispatchEvent(new CustomEvent('open'));
  }

  public closeDialog() {
    this.visible = false;
  }

  private handleCancel(e: MouseEvent) {
    e.preventDefault();
    this.visible = false;
    this.dispatchEvent(new CustomEvent('cancel'));
  }

  private handleSubmit(e: MouseEvent) {
    e.preventDefault();
    if ((<HTMLInputElement>e.target)?.disabled) {
      return;
    }
    this.visible = false;
    this.dispatchEvent(new CustomEvent('submit'));
  }

  private handleDelete(e: MouseEvent) {
    e.preventDefault();
    this.dispatchEvent(new CustomEvent('delete'));
  }
}
